import React from "react";
import { formatCurrency } from "../../utils/formatters";
import { cardBrandLabel } from "./CardManagementSection";

// Shared between PaymentHistorySection (embedded "Recent Payments" preview)
// and PaymentsScreen (full filterable /payments page) — kept in one place so
// the two never drift the way the old two-table charge-history/manual-
// payments design did.

// Status dot color + label — drives both the row's leading dot and its meta
// line, replacing an earlier two-pill layout (whose combined width, varying
// with label length, was the root cause of inconsistent row heights).
export const STATUS_STYLES = {
  succeeded: { color: "#1E7A34", label: "Succeeded" },
  failed: { color: "#B3261E", label: "Failed" },
  pending: { color: "#B7791F", label: "Pending" },
  skipped_no_amount: { color: "#626262", label: "Skipped" },
  skipped_no_card: { color: "#626262", label: "Skipped" },
  skipped_paid_manually: { color: "#626262", label: "Settled" },
  // Defence in depth: 'draft' rows (migration 066) are excluded from every
  // advertiser-facing query, so this should never render — but if one leaks,
  // show it neutrally rather than as a raw "draft" label. PaymentRow's
  // fallback ({ color: "#626262", label: row.status }) also handles it.
  draft: { color: "#626262", label: "Draft" },
};

// Exactly the columns normalizeRow reads — every advertiser-facing
// advertiser_payments select uses this instead of "*". Under advertiser-
// scoped RLS a `select("*")` genuinely ships the internal columns to the
// browser (they simply aren't rendered): external_payment_note is a finance
// reference typed by support, and pending_conversion_ids is an in-flight
// claim's resolved conversion ids. Neither is advertiser-facing data, so
// neither leaves the server.
export const PAYMENT_ROW_COLUMNS =
  "id, source, status, amount, created_at, failure_message, card_attempted, card_brand, card_last4";

export function amountLabel(amount) {
  return formatCurrency(Number(amount) || 0);
}

// "Visa •••• 4242" from the card snapshot columns (migration 056) — null if
// neither is set (older rows written before the snapshot existed, or no
// card was ever attempted).
function cardLabel(row) {
  if (!row.card_brand && !row.card_last4) return null;
  const brand = row.card_brand ? cardBrandLabel(row.card_brand) : "Card";
  return row.card_last4 ? `${brand} •••• ${row.card_last4}` : brand;
}

// advertiser_payments rows (source: 'automatic' | 'manual', migration 058)
// normalize to one display shape. Dated by created_at — the timestamp the
// attempt actually happened — not charge_date, which is only the business
// period an automatic charge's catch-up window covers.
//
// detail is derived from status first, not just card_attempted truthiness —
// each skip reason means something different and must read that way:
// skipped_no_amount ("nothing was owed, so nothing was attempted") must
// never look like skipped_no_card ("a real charge was skipped because no
// card is on file"), and skipped_paid_manually ("a manual payment already
// covered this before the job even tried") is its own distinct case.
export function normalizeRow(row) {
  const date = new Date(row.created_at);
  const label = cardLabel(row);
  let detail;
  if (row.status === "skipped_no_amount") {
    detail = "Nothing owed";
  } else if (row.status === "skipped_paid_manually") {
    detail = "Covered by a manual payment";
  } else if (row.status === "pending") {
    detail = "Awaiting confirmation";
  } else if (row.source === "automatic") {
    if (!row.card_attempted) {
      detail = "No card on file";
    } else {
      const role = row.card_attempted === "primary" ? "Primary" : "Backup";
      detail = label ? `${role} card — ${label}` : `${role} card`;
    }
  } else if (row.source === "external") {
    // source='external' rows (DEV-2116 WU-5, migration 073) are an offline
    // payment support recorded — never carry a card snapshot and are
    // deliberately never dated (charge_date is null, D18). Advertisers see a
    // neutral confirmation only; external_payment_note is a finance
    // reference (wire/bank ref) that is treated as internal — it is not
    // rendered here AND is not selected at all (see PAYMENT_ROW_COLUMNS
    // above), so it never reaches the advertiser's browser.
    detail = "Payment received";
  } else {
    detail = label || "Card payment";
  }
  return {
    key: `${row.source}-${row.id}`,
    source: row.source,
    displayDate: date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    amount: row.amount,
    status: row.status,
    detail,
    failureMessage: row.status === "failed" ? row.failure_message : null,
  };
}

// One payment row — status dot + amount + date, meta line, optional failure
// detail. Shared verbatim between the embedded preview and the full page.
export function PaymentRow({ row }) {
  const statusStyle = STATUS_STYLES[row.status] || { color: "#626262", label: row.status };
  const sourceLabel = row.source === "manual" ? "Manual" : row.source === "external" ? "Offline" : "Automatic";
  const metaParts = [statusStyle.label, sourceLabel];
  if (!row.failureMessage) metaParts.push(row.detail);
  return (
    <div style={{ padding:"12px 14px", marginBottom:8, background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:10 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ width:8, height:8, borderRadius:"50%", flexShrink:0, background:statusStyle.color }} />
        <span style={{ fontSize:14, fontWeight:600, color:"#151515" }}>{amountLabel(row.amount)}</span>
        <span style={{ fontSize:12, color:"#7b7b7b", flexShrink:0, whiteSpace:"nowrap", marginLeft:"auto" }}>{row.displayDate}</span>
      </div>
      <div style={{ marginTop:4, marginLeft:16, fontSize:12, color:"#626262" }}>
        {metaParts.join(" · ")}
      </div>
      {row.failureMessage && (
        <div style={{ marginTop:2, marginLeft:16, fontSize:12, color:"#e53935" }}>
          {row.detail} — {row.failureMessage}
        </div>
      )}
    </div>
  );
}
